import { Link2Icon } from '@radix-ui/react-icons';
import { useEditorState, type Editor } from '@tiptap/react';
import { HTMLAttributes, useCallback, useState } from 'react';

import { Button } from '../../../primitives/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../primitives/dialog';
import { Input } from '../../../primitives/input';
import { Toggle } from '../../../primitives/toggle';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../primitives/tooltip';
import { LabelWithoutRef } from '../../label/label-without-ref';

interface ToggleLinkProps extends Pick<HTMLAttributes<HTMLDivElement>, 'className'> {
  editor: Editor;
}

function validateUrl(value: string) {
  if (!value.trim()) return { value, error: '' };

  const httpsRegex = /^https:\/\/.+/i;
  if (!httpsRegex.test(value)) return { value, error: 'La URL debe comenzar con https://' };

  return { value, error: '' };
}

export function ToggleLink({ className, editor }: ToggleLinkProps) {
  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState({ value: '', error: '' });

  const { isLink } = useEditorState({
    editor,
    selector: ({ editor }: { editor: Editor }) => ({
      isLink: editor.isActive('link'),
    }),
  });

  const handleOpenDialog = () => {
    const currentUrl = editor.getAttributes('link').href || '';
    setForm(validateUrl(currentUrl));
    setOpenDialog(true);
  };

  const handleConfirm = useCallback(() => {
    if (form.error) return;

    const href = form.value.trim();

    if (href === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    }

    setOpenDialog(false);
  }, [editor, form]);

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <div>
            <Toggle
              aria-label="Agregar enlace"
              className={className}
              pressed={isLink}
              size="icon"
              variant="ghost"
              onPressedChange={handleOpenDialog}
            >
              <Link2Icon className="size-4" />
            </Toggle>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p className="flex flex-col">Agregar enlace</p>
        </TooltipContent>
      </Tooltip>

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="h-auto max-w-lg rounded-lg md:h-auto">
          <DialogHeader className="mb-8 px-1">
            <DialogTitle className="leading-7">Agregar enlace</DialogTitle>
            <DialogDescription>
              Ingresa la URL del enlace que deseas asignar al texto seleccionado.
            </DialogDescription>
          </DialogHeader>

          <section className="grid gap-4 px-1">
            <div className="space-y-1">
              <LabelWithoutRef>URL</LabelWithoutRef>
              <Input
                type="url"
                autoFocus
                placeholder="https://gbm.com.mx"
                value={form.value}
                onChange={(e) => setForm(validateUrl(e.target.value))}
                onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
              />
              {form.error && <p className="text-sm text-destructive">{form.error}</p>}
            </div>

            <div className="mt-8 flex flex-col-reverse gap-4 sm:flex-row sm:justify-end">
              <Button
                title="Cancelar"
                className="min-w-32"
                variant="destructive"
                onClick={() => setOpenDialog(false)}
              >
                Cancelar
              </Button>
              <Button
                title="Confirmar"
                className="min-w-32"
                disabled={form.error !== ''}
                onClick={handleConfirm}
              >
                Confirmar
              </Button>
            </div>
          </section>
        </DialogContent>
      </Dialog>
    </>
  );
}
